/**
 * AI Scribe V2 — design tokens & static config
 *
 * Every colour here resolves to a PRIMITIVE token — no raw hex in components.
 *
 * @author [Rajkumar Rathod]
 */

import { PRIMITIVE, px } from "../../../../../../../../../tokens/design-tokens";
import { CHART_COLORS } from "../../../../../../../../../components/app/provider/patient/chart";
import { ClinicalEventCategory } from "./types";

const c = PRIMITIVE.color;

/**
 * Master switch for the scribe experience.
 * `false` falls straight back to the V1 `AiScriberModal` — nothing else to change.
 */
export const IS_AI_SCRIBE_V2_ENABLED = true;
const SCRIBE_V2_API_BASE = "wss://aiengine.ecarehealth.com/ws/stream";
const SCRIBE_TOKEN = import.meta.env.VITE_SCRIBER_TOKEN;

/**
 * Streaming ASR socket for the live session.
 *
 * It decodes a **raw** sample stream — 16-bit LE PCM, mono, 16 kHz — so audio is
 * captured through `usePcmAudioStream`, not the V1 `audio/webm` recorder.
 */
export const SCRIBE_V2_AUDIO_SOCKET_URL =
  SCRIBE_V2_API_BASE + `?token=${SCRIBE_TOKEN}`;

// "wss://32.199.58.254:80/ws/stream?token=oCqMr60B0ZqaiAA06HCgQmPkk1pypYDbW5yq6kbPpYSXRjuLRpiipA6r2nqmtXWMcs0";

/** Surfaces, accents and geometry for the docked scribe window. */
export const SCRIBE_V2_COLORS = {
  /** Live/recording accent — window border, status text, wave bars */
  accentLive: c.pink[500],
  accentLiveSoft: c.pink["00"],
  /** Paused accent */
  accentPaused: c.orange[500],

  /** Window chrome — the scribe sits on a white surface */
  windowBg: c.neutrals["000"],
  windowBorderIdle: c.neutrals[1200],
  headerBg: c.neutrals["000"],
  panelBg: c.neutrals["000"],
  panelDivider: c.neutrals[900],

  /** Transcript bubbles — both lanes share a surface; the name colour separates them */
  bubbleBg: c.darkBlue[100],
  bubbleProviderName: CHART_COLORS.primary,
  bubblePatientName: c.pink[500],

  /** Text */
  textPrimary: CHART_COLORS.textDark,
  textSecondary: c.neutrals[2100],
  textMuted: c.neutrals[1900],
  panelLabel: c.neutrals[2200],

  /** Header stop control — destructive red, like other stop actions */
  stopGlyph: c.red[500],
  stopGlyphHover: c.red[600],
  stopGlyphDisabled: c.neutrals[1800],

  /** Draft review — the note sits as a document on a soft canvas */
  noteCanvasBg: c.neutrals[800],
  noteCardBorder: c.neutrals[900],
  noteSectionLabel: CHART_COLORS.primary,

  /** Instructions column */
  instructionsFieldBg: c.neutrals[300],
  instructionsFieldBorder: c.neutrals[1000],
  actionIconBg: c.green[100],
  actionIcon: c.green[700],
} as const;

/**
 * Geometry. The window is an absolutely-positioned overlay — never dragged —
 * inset from the edges of the visit-note panel that hosts it.
 */
export const SCRIBE_V2_DIMENSIONS = {
  /** Gap between the window and its host panel's edge */
  inset: PRIMITIVE.spacing[8],
  /** Header-only pill the window collapses to */
  minimizedWidth: 340,
  headerHeight: px(PRIMITIVE.spacing[44]),
  panelHeaderHeight: px(PRIMITIVE.spacing[36]),
  radius: px(PRIMITIVE.radius[8]),
  bubbleRadius: px(PRIMITIVE.radius[8]),
  /** Column flex ratios — transcript / events / instructions */
  transcriptFlex: 2.4,
  eventsFlex: 1,
  instructionsFlex: 1,
  /**
   * Floors so a narrow (un-expanded) visit-note panel crushes the columns into
   * unreadable slivers; below the combined floor the body scrolls instead.
   */
  /** Draft note document */
  noteCardMaxWidth: 880,
  noteCardRadius: px(PRIMITIVE.radius[8]),
  noteLabelTracking: "0.06em",
  noteLineHeight: 1.6,
  transcriptMinWidth: 300,
  eventsMinWidth: 220,
  instructionsMinWidth: 250,
  zIndex: 1300,
} as const;

/** Distance (px) from the bottom within which the transcript counts as pinned. */
export const SCRIBE_TRANSCRIPT_STICK_THRESHOLD_PX = 48;

/** sessionStorage key prefix + schema version for a recoverable live session. */
export const SCRIBE_SESSION_STORAGE_PREFIX = "scribe-v2-session:";
export const SCRIBE_SESSION_SNAPSHOT_VERSION = 1;

/**
 * AES-GCM parameters for the at-rest snapshot. The passphrase is defence in
 * depth — it keeps PHI out of plaintext in `sessionStorage`, not a boundary
 * against code running on the page, which can always derive the same key.
 */
export const SCRIBE_SESSION_CRYPTO_PASSPHRASE = "ech-scribe-v2-session-store";
export const SCRIBE_SESSION_PBKDF2_ITERATIONS = 100_000;
export const SCRIBE_SESSION_SALT_BYTES = 16;
export const SCRIBE_SESSION_IV_BYTES = 12;

export const SCRIBE_COPY_TRANSCRIPT_LABELS = {
  BUTTON: "Copy transcript",
  SUCCESS: "Transcript copied",
  FAILURE: "Couldn't copy the transcript",
} as const;

/** Copy for the live-transcript scroll control. */
export const SCRIBE_LIVE_TRANSCRIPT_LABELS = {
  JUMP_TO_LATEST: "Jump to latest",
  LISTENING: "Listening…",
} as const;

/** Label, accent and background for every clinical-event category. */
export const CLINICAL_EVENT_META: Record<
  ClinicalEventCategory,
  { label: string; accent: string; bg: string }
> = {
  SYMPTOM: { label: "Symptom", accent: c.red[500], bg: c.red["00"] },
  STRESSOR: { label: "Stressor", accent: c.orange[600], bg: c.orange["00"] },
  RISK_SCREEN: {
    label: "Risk Screen",
    accent: c.green[600],
    bg: c.green["00"],
  },
  MEDICATION: {
    label: "Medication",
    accent: c.orange[500],
    bg: c.orange["00"],
  },
  THERAPY: {
    label: "Therapy",
    accent: c.lightBlue[500],
    bg: c.lightBlue["00"],
  },
  PLAN: { label: "Plan", accent: c.brandBlue[700], bg: c.brandBlue[25] },
  FOLLOW_UP: { label: "Follow-up", accent: c.green[700], bg: c.green["00"] },
  DIAGNOSIS: { label: "Diagnosis", accent: c.violet[400], bg: c.violet["00"] },
  ALLERGY: { label: "Allergy", accent: c.red[400], bg: c.red["00"] },
  VITALS: { label: "Vitals", accent: c.teal[600], bg: c.teal["00"] },
  OTHER: { label: "Note", accent: c.neutrals[2100], bg: c.neutrals[400] },
};

/** Aliases the backend may send for a category, mapped to our canonical keys. */
export const CLINICAL_EVENT_CATEGORY_ALIASES: Record<
  string,
  ClinicalEventCategory
> = {
  SYMPTOM: "SYMPTOM",
  SYMPTOMS: "SYMPTOM",
  COMPLAINT: "SYMPTOM",
  CHIEF_COMPLAINT: "SYMPTOM",
  STRESSOR: "STRESSOR",
  STRESSORS: "STRESSOR",
  PSYCHOSOCIAL: "STRESSOR",
  RISK: "RISK_SCREEN",
  RISK_SCREEN: "RISK_SCREEN",
  SAFETY: "RISK_SCREEN",
  MEDICATION: "MEDICATION",
  MEDICATIONS: "MEDICATION",
  MEDS: "MEDICATION",
  THERAPY: "THERAPY",
  INTERVENTION: "THERAPY",
  PLAN: "PLAN",
  TREATMENT_PLAN: "PLAN",
  FOLLOW_UP: "FOLLOW_UP",
  FOLLOWUP: "FOLLOW_UP",
  DIAGNOSIS: "DIAGNOSIS",
  ASSESSMENT: "DIAGNOSIS",
  ALLERGY: "ALLERGY",
  ALLERGIES: "ALLERGY",
  VITALS: "VITALS",
  VITAL_SIGNS: "VITALS",
};

/** Speaker-name fragments used to infer a lane when the socket omits a role. */
export const PROVIDER_SPEAKER_HINTS = [
  "provider",
  "doctor",
  "dr.",
  "dr ",
  "clinician",
  "therapist",
  "physician",
];

export const PATIENT_SPEAKER_HINTS = ["patient", "client", "member"];

/** Default display names when the socket says nothing at all about the speaker. */
export const DEFAULT_SPEAKER_LABELS = {
  PROVIDER: "Provider",
  PATIENT: "Patient",
} as const;

/**
 * The diarisation index the socket treats as the first voice. The socket
 * numbers speakers from 1 (it no longer emits a zero-based index), so the first
 * voice takes the left/provider lane and any other index the right one.
 */
export const PRIMARY_DIARISATION_INDEX = 1;

/**
 * Shown when the socket identifies a speaker only by index. The label mirrors
 * whatever the socket diarised verbatim — `speaker: 1` reads as "Speaker 1",
 * `speaker: 3` as "Speaker 3", and so on — rather than collapsing everyone past
 * the first voice into a single second lane.
 */
export const diarisationSpeakerLabel = (index: number): string =>
  `Speaker ${index}`;
